import { useState, useEffect } from "react";
import {
  Group,
  Burger,
  Box,
  Drawer,
  Divider,
  Button,
  Collapse,
  UnstyledButton,
  ThemeIcon,
  useMantineTheme,
  Text,
  Center,
  ScrollArea,
  Container,
  Stack,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
// import { MantineLogo } from '@mantinex/mantine-logo';
import classes from "../styles//Header.module.css";
import Image from "next/image";
import { IconArrowLeft, IconChevronDown, IconScissors, IconBolt, IconBrush, IconUsers } from "@tabler/icons";
import useTranslation from "@/hooks/useTranslation";
import Link from "next/link";
import { useRouter } from "next/router";

import { HeaderMegaMenu } from "./HeaderMegaMenu";
import { CosmeticsIcon } from "./icons/CosmeticsIcon";

export function Header() {
  const { t } = useTranslation();
  const router = useRouter();
  const [scrolled, setScrolled] = useState(false);
  const links = [
    { label: t("Header")[0].label, link: "/" },
    { label: "אודות", link: "/about" },
    { label: "מרכז מידע", link: "/magazine" },
    { label: "מחירון", link: "/pricing" },
    { label: t("Header")[2].label, link: "/contact" },
  ];

  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] =
    useDisclosure(false);

  const [linksOpened, { toggle: toggleLinks }] = useDisclosure(false);

  // Scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Set active link based on current route
  useEffect(() => {
    setActive(router.asPath);
  }, [router.asPath]);

  const mockdata = [
    {
      icon: CosmeticsIcon,
      link: "/queue-management-beauticians",
      title: "קוסמטיקאיות",
      description: "גמישות מירבית להגדיר את המערכת לצרכים שלך",
    },
    {
      icon: IconScissors,
      link: "/managing-queues-hair-salons",
      title: "ניהול מספרות",
      description: "כל הכלים שמתאימים למספרות במקום אחד",
    },
    {
      icon: IconBrush,
      link: "/queue-management-nail-techs",
      title: "נייליסטיות",
      description: "ניהול תורים, מילוי חורים ומקדמות לנייליסטיות",
    },
    {
      icon: IconUsers,
      link: "/beauty-salon-management",
      title: "מכוני יופי",
      description: "יומן לכל עובדת, מלאי ודוחות במנוי אחד",
    },
  ];
  const theme = useMantineTheme();

  const subLinks = mockdata.map((item) => (
    <UnstyledButton
      component="a"
      href={item.link}
      className={classes.subLink}
      key={item.title}
    >
      <Group wrap="nowrap" align="flex-start">
        <ThemeIcon size={34} variant="default" radius="md">
          <item.icon size={22} color={theme.colors.blue[6]} />
        </ThemeIcon>
        <div>
          <Text size="sm" fw={500}>
            {item.title}
          </Text>
          <Text size="xs" c="dimmed">
            {item.description}
          </Text>
        </div>
      </Group>
    </UnstyledButton>
  ));
  const [active, setActive] = useState(links[0].link);
  const items = links.map((link, i) => {
    return (
      <Link
        key={i}
        href={link.link}
        className={classes.link}
        data-active={active === link.link || undefined}
        onClick={() => {
          setActive(link.link);
          closeDrawer();
        }}
      >
        <div style={{fontFamily: "'Rubik', 'Heebo', sans-serif"}}>{link.label}</div>
      </Link>
    );
  });

  return (
    <Box>
      <header className={`${classes.header} ${scrolled ? classes.scrolled : ""}`}>
        <Container size="xl" style={{ height: "100%" }}>
          <Group className={classes.inner} justify="space-between" wrap="nowrap">
            {/* Logo */}
            <Box className={classes.logoContainer}>
              <Link
                href="/"
                style={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                  position: "relative",
                }}
              >
                <Image
                  src="/testlogo-tight.svg"
                  fill
                  alt="לוגו QuickTor - המילה QuickTor בצבע כחול עם אייקון חץ מהיר, המסמל תור מהיר וזריז"
                  style={{
                    objectFit: "contain",
                    objectPosition: "center",
                  }}
                />
              </Link>
            </Box>

            {/* Navigation Links */}
            <Group gap="xs" visibleFrom="md" wrap="nowrap" style={{ flex: 1, justifyContent: "center", margin: "0 20px" }}>
              {items}
              <HeaderMegaMenu classes={classes} mockdata={mockdata} />
            </Group>

            {/* CTA Button */}
            <Group className={classes.ctaContainer}>
              <Button
                rightSection={<IconArrowLeft size={18} />}
                variant="filled"
                component="a"
                href="https://app.quicktor.co.il/MainPageToConnect/CreateBusinessAccount"
                radius="xl"
                fw={700}
                size="md"
                leftSection={<IconBolt size={18} />}
                style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                }}
              >
                להתחיל עכשיו
              </Button>
            </Group>

            {/* Mobile Burger */}
            <Burger
              opened={drawerOpened}
              onClick={toggleDrawer}
              hiddenFrom="md"
              size="md"
              className={classes.burger}
            />
          </Group>
        </Container>
      </header>

      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size="100%"
        padding={0}
        hiddenFrom="md"
        zIndex={1000000}
        styles={{
          content: {
            background: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(20px)",
          },
        }}
      >
        <ScrollArea h="100vh" style={{ padding: "var(--mantine-spacing-lg)" }}>
          {/* Mobile Logo */}
          <Box
            mb="xl"
            ta="center"
            style={{
              position: "relative",
              width: "100%",
              maxWidth: 280,
              height: 100,
              margin: "0 auto",
            }}
          >
            <Link
              href="/"
              onClick={closeDrawer}
              style={{
                display: "block",
                width: "100%",
                height: "100%",
                position: "relative",
              }}
            >
              <Image
                src="/testlogo-tight.svg"
                fill
                alt="לוגו QuickTor"
                style={{
                  objectFit: "contain",
                  objectPosition: "center",
                }}
                priority
              />
            </Link>
          </Box>

          <Divider mb="lg" />

          {/* Mobile Navigation Links */}
          <Stack gap="xs" mb="lg">
            {items.map((item, index) => (
              <Box key={index} onClick={closeDrawer}>
                {item}
              </Box>
            ))}
          </Stack>

          {/* Mobile Solutions Dropdown */}
          <UnstyledButton
            className={classes.mobileLink}
            onClick={toggleLinks}
            style={{ width: "100%" }}
          >
            <Center inline>
              <Box component="span" mr={8}>
                פתרונות לפי תחום
              </Box>
              <IconChevronDown
                size={16}
                style={{
                  transform: linksOpened ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              />
            </Center>
          </UnstyledButton>

          <Collapse in={linksOpened} mb="lg">
            <Box pl="md" pt="xs">
              {subLinks}
            </Box>
          </Collapse>

          <Divider my="lg" />

          {/* Mobile CTA Button */}
          <Group justify="center" mb="xl">
            <Button
              rightSection={<IconArrowLeft size={18} />}
              leftSection={<IconBolt size={18} />}
              variant="filled"
              radius="xl"
              fw={700}
              size="lg"
              fullWidth
              component="a"
              href="https://app.quicktor.co.il/MainPageToConnect/CreateBusinessAccount"
              onClick={closeDrawer}
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
                boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
              }}
            >
              להתחיל עכשיו - 14 יום חינם
            </Button>
          </Group>
        </ScrollArea>
      </Drawer>
    </Box>
  );
}
